import React, { useState, useEffect, useCallback } from 'react';
import { 
  Inbox, MessageSquare, RefreshCw, Loader2, Phone, User, Building2
} from 'lucide-react';
import type { Team, SalesRoute } from '../../types';
import { getInbox, type InboxItem } from '../../services/twilio';

interface InboxPageProps {
  team: Team;
  navigate: (route: SalesRoute, params?: Record<string, string>) => void;
}

const InboxPage: React.FC<InboxPageProps> = ({ team, navigate }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [inbox, setInbox] = useState<InboxItem[]>([]);

  const fetchInbox = useCallback(async () => {
    const data = await getInbox(team.id);
    setInbox(data);
  }, [team.id]);

  useEffect(() => {
    setLoading(true);
    fetchInbox().finally(() => setLoading(false));
  }, [fetchInbox]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchInbox();
    setRefreshing(false);
  };

  const handleContactClick = (contactId: string) => {
    navigate('leads', { contactId, tab: 'comms' });
  };

  const getChannelColor = (channel: 'whatsapp' | 'sms') => {
    return channel === 'whatsapp' ? 'text-green-400' : 'text-blue-400';
  };

  const getChannelBg = (channel: 'whatsapp' | 'sms') => {
    return channel === 'whatsapp' ? 'bg-green-500/20' : 'bg-blue-500/20';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Inbox size={24} className="text-orange-500" />
            Inbox
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Recent conversations across WhatsApp and SMS
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors"
        >
          <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <div className="text-2xl font-bold text-white">{inbox.length}</div>
          <div className="text-sm text-slate-400">Conversations</div>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <div className="text-2xl font-bold text-green-400">
            {inbox.filter(i => i.channel === 'whatsapp').length}
          </div>
          <div className="text-sm text-slate-400">WhatsApp</div>
        </div>
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-4">
          <div className="text-2xl font-bold text-blue-400">
            {inbox.filter(i => i.channel === 'sms').length}
          </div>
          <div className="text-sm text-slate-400">SMS</div>
        </div>
      </div>

      {/* Conversation List */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
          <h2 className="font-semibold text-white flex items-center gap-2">
            <MessageSquare size={18} className="text-orange-500" />
            Recent Conversations
          </h2>
          <span className="text-sm text-slate-400">{inbox.length} total</span>
        </div>

        {inbox.length === 0 ? (
          <div className="p-8 text-center">
            <MessageSquare size={48} className="mx-auto mb-4 text-slate-600" />
            <h3 className="text-lg font-medium text-white mb-2">No conversations yet</h3>
            <p className="text-slate-400 text-sm">
              Messages will appear here when you send or receive WhatsApp and SMS messages.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {inbox.map((item) => (
              <button
                key={item.contact_id}
                onClick={() => handleContactClick(item.contact_id)}
                className="w-full px-5 py-4 flex items-start gap-4 hover:bg-slate-800/50 transition-colors text-left"
              >
                {/* Avatar */}
                <div className="w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center shrink-0">
                  <User size={20} className="text-slate-400" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${item.unread ? 'text-white' : 'text-slate-300'}`}>
                        {item.contact_name}
                      </span>
                      {item.unread && (
                        <span className="w-2 h-2 bg-orange-500 rounded-full" />
                      )}
                    </div>
                    <span className="text-xs text-slate-500 shrink-0">
                      {new Date(item.last_message_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>

                  {item.company && (
                    <div className="flex items-center gap-1 mt-0.5 text-xs text-slate-500">
                      <Building2 size={12} />
                      {item.company}
                    </div>
                  )}

                  <div className="flex items-start gap-2 mt-1">
                    <span className={`shrink-0 text-xs px-1.5 py-0.5 rounded ${getChannelBg(item.channel)} ${getChannelColor(item.channel)}`}>
                      {item.channel.toUpperCase()}
                    </span>
                    <p className={`text-sm line-clamp-2 ${item.unread ? 'text-slate-300' : 'text-slate-500'}`}>
                      {item.direction === 'outbound' && <span className="text-slate-500">You: </span>}
                      {item.last_message}
                    </p>
                  </div>
                </div>

                {/* Direction indicator */}
                <div className="shrink-0">
                  {item.direction === 'inbound' ? (
                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                      <Phone size={14} className="text-green-400 rotate-[-135deg]" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <Phone size={14} className="text-blue-400 rotate-[135deg]" />
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InboxPage;
